import type {Config} from "tailwindcss";
import twAriaPlugin from "tailwindcss-react-aria-components";
import twTypography from "@tailwindcss/typography";

// https://tailwindcss.com/docs/configuration

/**
 * Construct a Roboto font variant with the given variation settings.
 *
 * See https://fonts.google.com/variablefonts?vfquery=roboto+flex#font-families
 * for the available axes, their definitions, and their ranges.
 */
const makeRobotoVariantWithVariation = (
	fontVariationSettings: string
): [string, {fontFeatureSettings: string; fontVariationSettings: string}] => {
	return [
		"var(--font-roboto-flex), Roboto, sans-serif",
		{
			fontFeatureSettings: "'clig' off, 'liga' off",
			fontVariationSettings,
		},
	];
};

export default {
	content: [
		"./app/**/*.{js,ts,jsx,tsx,mdx}",
		"./.storybook/**/*.{js,ts,jsx,tsx,mdx}",
	],
	/**
	 * Tailwind theme options are based on the style guide at
	 * https://www.figma.com/file/9PVWxIt9N4YZkLPw0aJ4OH/PC-Sell-Pages-Core-Styles-and-Components?node-id=8%3A5810&mode=dev
	 */
	theme: {
		colors: {
			white: "#ffffff",
			navy: {
				25: "#fdfcfc",
				50: "#f8faf9",
				100: "#e1f2ef",
				200: "#d5efec",
				300: "#cbe8e8",
				400: "#b2dbde",
				500: "#abd1da",
				600: "#6facc5",
				700: "#5f90b2",
				800: "#325c7f",
				900: "#203c53",
			},
			"pale-light-cyan": "#e1f2ef",
			"midtone-cyan": "#abd1da",
			hyperlink: "#3075a5",
			"midtone-blue": "#325c7f",
			"dark-navy": "#1a375d",
			green: {
				25: "#fdfcfc",
				50: "#f7f9f3",
				100: "#f2f7e1",
				200: "#eaf1cb",
				300: "#e2ecbc",
				400: "#d0de9b",
				500: "#c3d870",
				600: "#9cbd3d",
				700: "#6b9b3b",
				800: "#3f6d37",
				900: "#354c2d",
			},
			"pale-lime-green": "#f2f7e1",
			"light-green": "#c3d870",
			"linguistic-lime": "#7dad4d",
			"lime-rind": "#3f6d37",
			"dark-green": "#354c2d",
			orange: {
				25: "#fdfcfc",
				50: "#f9f7f6",
				100: "#faede4",
				200: "#fae2d7",
				300: "#fad5c0",
				400: "#ffc6ae",
				500: "#f2a989",
				600: "#eb7a56",
				700: "#f04e29",
				800: "#a14429",
				900: "#722e1e",
			},
			"pale-peach": "#faede4",
			"muted-midtone-orange": "#ec8456",
			"rich-orange": "#f04e29",
			"brick-orange": "#b34c2f",
			"dark-orange": "#943c27",
			gray: {
				25: "#fdfcfc",
				50: "#f5f4f3",
				100: "#f1f0ee",
				200: "#ebe9e5",
				300: "#e5e3dc",
				400: "#dedad1",
				500: "#bebbb4",
				600: "#9d9b96",
				700: "#777674",
				800: "#525152",
				900: "#2c2b2f",
			},
			corporate: {
				// TODO: fill the rest of this out based on the Styles Library Figma if we need it
				// https://www.figma.com/file/DLOC0occpjiXi9n8Ts1Iwe/Styles-Library-(Current)?type=design&node-id=120-1654
				gray: {
					100: "#ffffff",
					200: "#f5f7fa",
					300: "#edeff2",
					400: "#c2c6cc",
					500: "#797c80",
					600: "#494b4d",
					700: "#262626",
				},
			},
			semantic: {
				error: {
					100: "#FFF4F2",
					300: "#DC2E1E",
					500: "#9C1407",
				},
				info: {
					100: "#EBF9F9",
					300: "#2DCCD2",
					500: "#013133",
				},
			},
			transparent: "transparent",
			current: "currentColor",
		},
		fontSize: {
			"display-7": "4.5rem",
			"display-6": "4rem",
			"display-5": "3.75rem",
			"9xl": "3.75rem",
			"display-4": "3.375rem",
			"8xl": "3.375rem",
			"display-3": "3rem",
			"7xl": "3rem",
			"display-2": "2.625rem",
			"6xl": "2.625rem",
			"display-1": "2.25rem",
			"5xl": "2.25rem",
			h1: "2rem",
			"4xl": "2rem",
			h2: "1.75rem",
			quote: "1.75rem",
			"3xl": "1.75rem",
			h3: "1.5rem",
			"2xl": "1.5rem",
			h4: "1.25rem",
			xl: "1.25rem",
			"body-3": "1.25rem",
			h5: "1.125rem",
			lg: "1.125rem",
			"body-2": "1.125rem",
			h6: "1rem",
			md: "1rem",
			"body-1": "1rem",
			sm: "0.875rem",
			button: "0.875rem",
			xs: "0.75rem",
		},
		fontFamily: {
			// CSS font-weight doesn't work with variable fonts, so we have to make a separate variant
			thin: makeRobotoVariantWithVariation("'wght' 200, 'wdth' 110"),
			sans: makeRobotoVariantWithVariation("'wght' 300, 'wdth' 110"),
			title: makeRobotoVariantWithVariation("'wght' 453, 'wdth' 110"),
			bold: makeRobotoVariantWithVariation("'wght' 600, 'wdth' 110"),
			extrabold: makeRobotoVariantWithVariation("'wght' 700, 'wdth' 110"),
			overline: makeRobotoVariantWithVariation(
				"'wght' 453, 'wdth' 110, 'yopq' 93, 'ytuc' 693, 'opsz' 16"
			),
			serif: "var(--font-pt-serif), serif",
		},
		screens: {
			// Bootstrap defaults https://getbootstrap.com/docs/5.0/layout/breakpoints/
			xs: "428px",
			sm: "576px",
			md: "768px",
			lg: "992px",
			xl: "1280px",
			"2xl": "1400px",
		},
		container: {
			center: true,
		},
		extend: {
			backgroundImage: {
				"loading-gradient":
					"linear-gradient(90deg, #ebe9e5 40%, #f5f4f3 50%, #ebe9e5 60%)",
			},
			backgroundSize: {
				"200%": "200%",
			},
			borderRadius: {
				"lg.5": "0.75rem",
			},
			boxShadow: {
				"request-information": "0px 4px 24px 0px rgba(119, 118, 116, 0.50)",
			},
			gridTemplateRows: {
				"2-auto": "repeat(2, minmax(0, auto))",
				"3-auto": "repeat(3, minmax(0, auto))",
			},
			gridTemplateColumns: {
				"2-auto": "repeat(2, minmax(0, auto))",
				"3-auto": "repeat(3, minmax(0, auto))",
			},
			spacing: {
				"1px": "1px",
				30: "7.5rem",
				// Used for a negative margin hack in MobileDuoFilterModal
				"duo-padx": "1rem",
				navbar: "4rem",
			},
			strokeWidth: {
				3: "3",
			},
			zIndex: {
				// Leaflet maps are rendered in a div with a max z-index of 700
				// https://leafletjs.com/reference.html#map-pane
				"campus-map": "700",
				"course-filter-header": "1000",
				"hello-bar": "2000",
				navbar: "3000",
				modal: "8000",
				"mobile-menu": "10000",
			},
			lineHeight: {
				// Line heights
				1.3: "1.3",
				1.4: "1.4",
				1.5: "1.5",
			},
			borderWidth: {
				1: "1px",
			},
			animation: {
				fade: "fade 0.3s ease-in",
				"fade-out": "fade 0.3s ease-in reverse",
				"fade-up": "fade-up 0.3s ease-in",
				"fade-up-reverse": "fade-up 0.3s ease-in reverse",
				"loading-l-to-r": "slide-l-to-r 1.5s infinite linear",
			},
			keyframes: {
				fade: {
					"0%": {opacity: "0"},
					"100%": {opacity: "1"},
				},
				"fade-up": {
					"0%": {opacity: "0", transform: "translateY(0.25rem)"},
					"100%": {opacity: "1", transform: "translateY(0)"},
				},
				"slide-l-to-r": {
					"0%": {backroundPosition: "-100% 0"},
					"100%": {backgroundPosition: "100% 0"},
				},
			},
			transitionProperty: {
				height: "height",
			},
			typography: {
				DEFAULT: {
					css: {
						maxWidth: "auto",
					},
				},
			},
		},
	},
	plugins: [twAriaPlugin, twTypography],
} satisfies Config;
